import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { issueTicketTypes } from 'constants/issueTickets';
import { SelectField } from 'admin-components/components';
import { StyledRadioFieldSet, StyledWarningMessage } from '../GenerateTickets.styles';
import { brandCode } from 'constants/brandCodes';
import { bookingShape } from 'constants/propTypes';

const PaddedConditionalChild = styled('div')`
  margin: 0.5em 1em 2em 1em;
`;

const showWarningOnSNCBOption = [issueTicketTypes.EMAIL_PDF, issueTicketTypes.DOWNLOAD_PDF, issueTicketTypes.PRINT_ATB];
const hasSNCBLeg = (journey) => journey.filter((j) => !j.cancelled)?.some((leg) => leg.serviceType?.brandCode === brandCode.SNCB);

const TicketOptions = ({
  selectedMethod,
  setSelectedMethod,
  allowedMethods,
  detailsCaptured,
  passengerEmails,
  selectedEmails,
  setSelectedEmails,
  booking,
  hasSNCBTicketsUnavailable = false
}) => {
  const radioButtons = [
    {
      label: <FormattedMessage id="ticketMethods.printAtb" />,
      value: issueTicketTypes.PRINT_ATB
    },
    {
      label: <FormattedMessage id="ticketMethods.printTerminal" />,
      value: issueTicketTypes.PRINT_ADYEN_TERMINAL
    },
    {
      label: <FormattedMessage id="ticketMethods.emailPdf" />,
      value: issueTicketTypes.EMAIL_PDF,
      disabled: !detailsCaptured
    },
    {
      label: <FormattedMessage id="ticketMethods.downloadPdf" />,
      value: issueTicketTypes.DOWNLOAD_PDF,
      disabled: !detailsCaptured
    }
  ];

  const emailOptions = passengerEmails.map((value) => ({ label: value, value }));

  return detailsCaptured || [issueTicketTypes.PRINT_ATB, issueTicketTypes.PRINT_ADYEN_TERMINAL].includes(selectedMethod) ? (
    <div className="sausage" data-testid="ticket-options" data-details-captured={detailsCaptured ? 'true' : 'false'}>
      <StyledRadioFieldSet
        direction="column"
        name="ticketMethod"
        selectedValue={selectedMethod}
        onChange={(e) => setSelectedMethod(e.target.value)}
        radioButtons={allowedMethods.map((method) => radioButtons.find(({ value }) => value === method))}
        optionalChildren={{
          [issueTicketTypes.EMAIL_PDF]: (
            <PaddedConditionalChild>
              <SelectField
                id="ticket-options-email-select"
                value={selectedEmails}
                options={emailOptions}
                multiple
                onChange={(newEmails) => newEmails.length && setSelectedEmails(newEmails)}
                disabled={selectedMethod !== issueTicketTypes.EMAIL_PDF}
              />
            </PaddedConditionalChild>
          )
        }}
      />
      {hasSNCBLeg([...(booking?.outbound ?? []), ...(booking?.inbound ?? [])]) && showWarningOnSNCBOption.includes(selectedMethod) && (
        <StyledWarningMessage>
          {selectedMethod === issueTicketTypes.PRINT_ATB && (
            <>
              <FormattedMessage id="printWarning.SNCB.atb" />
              <br />
              <br />
            </>
          )}
          <FormattedMessage id="printWarning.SNCB" />
        </StyledWarningMessage>
      )}

      {hasSNCBTicketsUnavailable && (
        <StyledWarningMessage>
          <FormattedMessage id="issueTickets.externalTicketsNotReady" />
        </StyledWarningMessage>
      )}
    </div>
  ) : (
    <div data-cy="missing-details">
      <FormattedMessage id="ticketMethods.missingDetails" />
    </div>
  );
};

TicketOptions.propTypes = {
  selectedMethod: PropTypes.string,
  setSelectedMethod: PropTypes.func,
  allowedMethods: PropTypes.arrayOf(PropTypes.string),
  detailsCaptured: PropTypes.bool,
  passengerEmails: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedEmails: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedEmails: PropTypes.func.isRequired,
  booking: bookingShape,
  hasSNCBTicketsUnavailable: PropTypes.bool
};

export default TicketOptions;
